import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ARTICLES from '../data/articles';

function formatDate(isoDate, language) {
  const [year, month, day] = isoDate.slice(0, 10).split('-').map(Number);
  return language === 'ja'
    ? `${year}年${month}月${day}日`
    : new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
}

// Contributed articles: one framed strip of thumbnail cards that scrolls
// sideways (swipe, trackpad, or the arrow buttons).
function WritingStrip({ language }) {
  const trackRef = useRef(null);
  const [edges, setEdges] = useState({ atStart: true, atEnd: false });
  const [sort, setSort] = useState('newest');

  const articles = useMemo(
    () =>
      [...ARTICLES].sort((left, right) =>
        sort === 'newest' ? right.date.localeCompare(left.date) : left.date.localeCompare(right.date)
      ),
    [sort]
  );

  const updateEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }
    const maxScroll = track.scrollWidth - track.clientWidth;
    setEdges({ atStart: track.scrollLeft <= 4, atEnd: track.scrollLeft >= maxScroll - 4 });
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges]);

  // A new order starts the strip from its first card again.
  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.scrollLeft = 0;
    }
    updateEdges();
  }, [sort, updateEdges]);

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    const card = track?.querySelector('.writing-card');
    if (!track || !card) {
      return;
    }
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  const copy =
    language === 'ja'
      ? {
          label: 'WRITING',
          title: '寄稿記事',
          sortLabel: '並び順',
          newest: '新しい順',
          oldest: '古い順',
          media: 'Stacknot',
          prev: '前の記事へ',
          next: '次の記事へ',
          listLabel: '寄稿記事の一覧',
        }
      : {
          label: 'WRITING',
          title: 'Writing',
          sortLabel: 'Sort',
          newest: 'Newest',
          oldest: 'Oldest',
          media: 'Stacknot',
          prev: 'Previous article',
          next: 'Next article',
          listLabel: 'Contributed articles',
        };

  return (
    <div id="writing" className="works-writing">
      <div className="works-archive__head">
        <p className="section-head__index mono">06 — {copy.label}</p>
        <h3 className="works-archive__title">{copy.title}</h3>
      </div>

      <div className="works-controls__sort works-writing__sort" role="group" aria-label={copy.sortLabel}>
        {['newest', 'oldest'].map((key) => (
          <button
            key={key}
            type="button"
            className={`chip ${sort === key ? 'is-active' : ''}`}
            onClick={() => setSort(key)}
            aria-pressed={sort === key}
          >
            {copy[key]}
          </button>
        ))}
      </div>

      <div className="works-writing__frame">
        <ul
          className="works-writing__track"
          ref={trackRef}
          onScroll={updateEdges}
          aria-label={copy.listLabel}
          tabIndex={0}
        >
          {articles.map((article) => (
            <li key={article.id} className="writing-card">
              <a className="writing-card__link" href={article.url} target="_blank" rel="noopener noreferrer">
                <img
                  className="writing-card__thumb"
                  src={article.image}
                  alt=""
                  loading="lazy"
                  width="1024"
                  height="683"
                />
                <span className="writing-card__body">
                  <span className="writing-card__meta mono">
                    {copy.media} · {formatDate(article.date, language)}
                  </span>
                  <span className="writing-card__title">{article.title}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>

        <div className="works-writing__nav">
          <button
            type="button"
            className="works-writing__arrow"
            onClick={() => scrollByCard(-1)}
            disabled={edges.atStart}
            aria-label={copy.prev}
          >
            ←
          </button>
          <button
            type="button"
            className="works-writing__arrow"
            onClick={() => scrollByCard(1)}
            disabled={edges.atEnd}
            aria-label={copy.next}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}

export default WritingStrip;
